import { computed, ref, watch } from "vue";
import { getAnalytics } from "../services/analytics.service";

function normalizeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function useAnalytics(initialInterval = "this_month") {
  const interval = ref(initialInterval);
  const loading = ref(false);
  const error = ref("");
  const payload = ref(null);

  const data = computed(() => payload.value?.data || null);
  const overview = computed(() => data.value?.overview || emptyOverview());
  const incidences = computed(() => data.value?.incidences || []);
  const attemptsDistribution = computed(() => data.value?.attempts_distribution || []);
  const dateRange = computed(() => data.value?.date_range || null);

  const isEmpty = computed(() => {
    const current = overview.value;
    const incidenceItems = incidences.value;
    const numericValues = [
      current.total_stops,
      current.total_deliveries,
      current.total_pickups,
      current.total_plans,
      current.total_routes,
      current.active_vehicles,
      current.completed_stops,
      current.partial_stops,
      current.incidence_stops,
      current.cancelled_stops,
      current.unassigned_stops,
      current.assigned_stops,
      current.retry_stops,
      current.avg_attempts,
      current.success_rate,
      current.incidence_rate,
      current.first_attempt_success_rate,
    ];
    return Boolean(payload.value?.valid) && numericValues.every((value) => normalizeNumber(value) === 0) && incidenceItems.length === 0;
  });

  async function load() {
    loading.value = true;
    error.value = "";
    try {
      const response = await getAnalytics(interval.value);
      const normalizedResponse = normalizeAnalyticsResponse(response, interval.value);
      payload.value = normalizedResponse;
      if (normalizedResponse?.valid === false) {
        error.value = normalizedResponse?.message || "No se pudo cargar Analytics.";
      }
    } catch (err) {
      payload.value = null;
      error.value =
        err?.message ||
        err?.messageHtml ||
        err?.messageText ||
        err?.error ||
        (typeof err === "string" ? err : "No se pudo cargar Analytics.");
    } finally {
      loading.value = false;
    }
  }

  watch(interval, () => {
    void load();
  });

  function retry() {
    void load();
  }

  return {
    interval,
    loading,
    error,
    payload,
    data,
    overview,
    incidences,
    attemptsDistribution,
    dateRange,
    isEmpty,
    load,
    retry,
  };
}

function emptyOverview() {
  return {
    total_stops: 0,
    total_deliveries: 0,
    total_pickups: 0,
    total_plans: 0,
    total_routes: 0,
    active_vehicles: 0,
    completed_stops: 0,
    partial_stops: 0,
    incidence_stops: 0,
    cancelled_stops: 0,
    unassigned_stops: 0,
    assigned_stops: 0,
    retry_stops: 0,
    avg_attempts: 0,
    success_rate: 0,
    incidence_rate: 0,
    first_attempt_success_rate: 0,
  };
}

function normalizeAnalyticsResponse(response, fallbackInterval) {
  const root = isObject(response) ? response : {};
  const nestedData = isObject(root.data) ? root.data : null;

  const dataCandidate =
    nestedData && isObject(nestedData.overview)
      ? nestedData
      : isObject(root.overview)
        ? root
        : nestedData && isObject(nestedData.data)
          ? nestedData.data
          : null;

  const normalizedData = normalizeAnalyticsData(dataCandidate, fallbackInterval);
  const hasUsableData =
    Boolean(normalizedData.date_range) ||
    Object.values(normalizedData.overview).some((value) => normalizeNumber(value) !== 0) ||
    normalizedData.incidences.length > 0;

  return {
    valid: typeof root.valid === "boolean" ? root.valid : hasUsableData,
    message: String(root.message || ""),
    data: normalizedData,
  };
}

function normalizeAnalyticsData(data, fallbackInterval) {
  const source = isObject(data) ? data : {};
  return {
    interval: String(source.interval || fallbackInterval || "this_month"),
    date_range: normalizeDateRange(source.date_range || source.dateRange),
    overview: normalizeOverview(source.overview),
    incidences: normalizeIncidences(source.incidences),
    attempts_distribution: normalizeAttemptsDistribution(source.attempts_distribution || source.attemptsDistribution),
  };
}

function normalizeDateRange(value) {
  const source = isObject(value) ? value : {};
  return {
    date_from: source.date_from || source.dateFrom || "",
    date_to: source.date_to || source.dateTo || "",
  };
}

function normalizeOverview(value) {
  const source = isObject(value) ? value : {};
  return {
    total_stops: normalizeNumber(source.total_stops ?? source.totalStops),
    total_deliveries: normalizeNumber(source.total_deliveries ?? source.totalDeliveries),
    total_pickups: normalizeNumber(source.total_pickups ?? source.totalPickups),
    total_plans: normalizeNumber(source.total_plans ?? source.totalPlans),
    total_routes: normalizeNumber(source.total_routes ?? source.totalRoutes),
    active_vehicles: normalizeNumber(source.active_vehicles ?? source.activeVehicles),
    completed_stops: normalizeNumber(source.completed_stops ?? source.completedStops),
    partial_stops: normalizeNumber(source.partial_stops ?? source.partialStops),
    incidence_stops: normalizeNumber(source.incidence_stops ?? source.incidenceStops),
    cancelled_stops: normalizeNumber(source.cancelled_stops ?? source.cancelledStops),
    unassigned_stops: normalizeNumber(source.unassigned_stops ?? source.unassignedStops),
    assigned_stops: normalizeNumber(source.assigned_stops ?? source.assignedStops),
    retry_stops: normalizeNumber(source.retry_stops ?? source.retryStops),
    avg_attempts: normalizeNumber(source.avg_attempts ?? source.avgAttempts),
    success_rate: normalizeNumber(source.success_rate ?? source.successRate),
    incidence_rate: normalizeNumber(source.incidence_rate ?? source.incidenceRate),
    first_attempt_success_rate: normalizeNumber(source.first_attempt_success_rate ?? source.firstAttemptSuccessRate),
  };
}

function normalizeIncidences(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const source = isObject(item) ? item : {};
    return {
      status_description: String(
        source.status_description || source.statusDescription || source.label || ""
      ),
      total: normalizeNumber(source.total ?? source.value),
    };
  });
}

function normalizeAttemptsDistribution(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const source = isObject(item) ? item : {};
    return {
      attempts: source.attempts ?? "",
      total: normalizeNumber(source.total ?? source.value),
    };
  });
}

function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
